import { modal } from "../../design/components.js";
import { hazardIcon } from "./hazard-icons.js";
import type { RawReportPoint } from "./raw-report-model.js";
import { warnungBundUrl } from "./warning-link.js";

export interface ReportDetailCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  report: RawReportPoint;
  onBack: () => void;
}

/** Detail panel for a SINGLE raw report (power-user opt-in). A raw report is not an
 *  incident — it is one uncorroborated observation — so this shows the report's own CAP
 *  classification (category/event via the hazard bucket label), response type, severity,
 *  observed time and source feed, with an explicit "unverified" note. */
export function reportDetail(ctx: ReportDetailCtx): HTMLElement {
  const { t, report } = ctx;
  const content = document.createElement("div");
  content.className = "report-detail";

  const head = document.createElement("p");
  head.className = "bx-headline report-detail__hazard";
  const icon = hazardIcon(report.hazard);
  icon.classList.add("report-detail__icon");
  head.append(icon, document.createTextNode(t(`hazard.${report.hazard}`)));
  content.append(head);

  const note = document.createElement("p");
  note.className = "bx-microlabel report-detail__unverified";
  note.textContent = t("now.report.unverified");
  content.append(note);

  const fields: Array<[string, string | undefined]> = [
    [t("now.report.event"), report.event],
    [t("now.report.response"), report.responseType],
    [t("now.report.severity"), report.severity],
    [t("now.report.observed"), report.observedAt],
    [t("now.report.source"), report.feed],
  ];
  const dl = document.createElement("div");
  dl.className = "report-detail__fields";
  for (const [label, value] of fields) {
    if (!value) continue;
    const row = document.createElement("div");
    row.className = "report-detail__row";
    const l = document.createElement("span");
    l.className = "bx-microlabel";
    l.textContent = label;
    const v = document.createElement("span");
    v.className = "bx-body";
    v.textContent = value;
    row.append(l, v);
    dl.append(row);
  }
  content.append(dl);

  // Deep link to the original civil-protection warning (NINA/DWD → warnung.bund.de).
  const warningUrl = warnungBundUrl(report.feed, report.id);
  if (warningUrl) {
    const a = document.createElement("a");
    a.className = "report-detail__source-link bx-body";
    a.href = warningUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = t("now.warning.viewSource");
    content.append(a);
  }

  return modal({ title: t("now.report.title"), content, onClose: ctx.onBack, sheetId: "report-detail" }) as HTMLElement;
}
